import { useMemo, useState } from 'react';
import {
  getMasteryProgressionSummary,
  getMasterySkillTimeline,
  getMasteryStudentOverview,
} from '../api/analytics';
import { parseJsonSafe } from '../api/client';

function getSummaryValue(overview, key) {
  if (!overview || typeof overview !== 'object') {
    return '';
  }
  if (overview[key] !== undefined && overview[key] !== null) {
    return overview[key];
  }
  if (overview.totals && overview.totals[key] !== undefined && overview.totals[key] !== null) {
    return overview.totals[key];
  }
  if (overview.summary && overview.summary[key] !== undefined && overview.summary[key] !== null) {
    return overview.summary[key];
  }
  return '';
}

function getSkills(overview) {
  if (!overview || typeof overview !== 'object') {
    return [];
  }
  if (Array.isArray(overview.skills)) {
    return overview.skills;
  }
  if (overview.summary && Array.isArray(overview.summary.skills)) {
    return overview.summary.skills;
  }
  return [];
}

function getEvidenceIntegritySummary(progressionSummary) {
  if (
    !progressionSummary ||
    typeof progressionSummary !== 'object' ||
    !progressionSummary.evidence_integrity_summary ||
    typeof progressionSummary.evidence_integrity_summary !== 'object'
  ) {
    return null;
  }
  return progressionSummary.evidence_integrity_summary;
}

function getProgressionIntegrityDiagnostics(progressionSummary) {
  if (
    !progressionSummary ||
    typeof progressionSummary !== 'object' ||
    !progressionSummary.progression_integrity_diagnostics ||
    typeof progressionSummary.progression_integrity_diagnostics !== 'object'
  ) {
    return null;
  }
  return progressionSummary.progression_integrity_diagnostics;
}

function getIntegrityReviewSignals(progressionSummary) {
  if (
    !progressionSummary ||
    typeof progressionSummary !== 'object' ||
    !progressionSummary.integrity_review_signals ||
    typeof progressionSummary.integrity_review_signals !== 'object'
  ) {
    return null;
  }
  return progressionSummary.integrity_review_signals;
}

function getProgressionTrustAdvisory(progressionSummary) {
  if (
    !progressionSummary ||
    typeof progressionSummary !== 'object' ||
    !progressionSummary.progression_trust_advisory ||
    typeof progressionSummary.progression_trust_advisory !== 'object'
  ) {
    return null;
  }
  return progressionSummary.progression_trust_advisory;
}

function formatPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '0%';
  }
  return `${Math.round(numeric * 100)}%`;
}

async function getErrorMessage(response, fallbackMessage) {
  const payload = await parseJsonSafe(response);
  if (payload && typeof payload === 'object') {
    if (typeof payload.detail === 'string' && payload.detail.trim()) {
      return payload.detail;
    }
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message;
    }
  }
  return fallbackMessage;
}

function MasteryAnalyticsPage() {
  const [studentIdInput, setStudentIdInput] = useState('');
  const [loadedStudentId, setLoadedStudentId] = useState('');
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState('');
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');
  const [timelineSkillId, setTimelineSkillId] = useState('');
  const [progressionSummary, setProgressionSummary] = useState(null);
  const [progressionError, setProgressionError] = useState('');

  const skills = useMemo(() => getSkills(overview), [overview]);
  const summaryCards = [
    { label: 'skills_total', value: String(getSummaryValue(overview, 'skills_total') ?? '') },
    { label: 'due_now', value: String(getSummaryValue(overview, 'due_now') ?? '') },
    { label: 'weak', value: String(getSummaryValue(overview, 'weak') ?? '') },
    { label: 'mastered', value: String(getSummaryValue(overview, 'mastered') ?? '') },
    { label: 'struggling', value: String(getSummaryValue(overview, 'struggling') ?? '') },
  ];

  const handleLoadOverview = async () => {
    const trimmed = studentIdInput.trim();
    if (!/^\d+$/.test(trimmed)) {
      setOverviewError('Please enter a numeric student id.');
      return;
    }

    setOverviewLoading(true);
    setOverviewError('');
    setOverview(null);
    setTimeline([]);
    setTimelineError('');
    setTimelineSkillId('');
    setProgressionSummary(null);
    setProgressionError('');

    try {
      const response = await getMasteryStudentOverview(trimmed);
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to load mastery overview.');
        setOverviewError(message);
        return;
      }
      const data = await parseJsonSafe(response);
      setOverview(data);
      setLoadedStudentId(trimmed);

      const progressionResponse = await getMasteryProgressionSummary(trimmed);
      if (!progressionResponse.ok) {
        const message = await getErrorMessage(
          progressionResponse,
          'Failed to load progression summary.'
        );
        setProgressionError(message);
        return;
      }
      const progressionData = await parseJsonSafe(progressionResponse);
      setProgressionSummary(progressionData);
    } catch (error) {
      setOverviewError(error.message || 'Failed to load mastery overview.');
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleLoadTimeline = async (skillId) => {
    if (!loadedStudentId) {
      return;
    }

    setTimelineLoading(true);
    setTimelineError('');
    setTimeline([]);
    setTimelineSkillId(skillId);

    try {
      const response = await getMasterySkillTimeline(loadedStudentId, skillId, { limit: 50 });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to load skill timeline.');
        setTimelineError(message);
        return;
      }
      const data = await parseJsonSafe(response);
      setTimeline(Array.isArray(data) ? data : []);
    } catch (error) {
      setTimelineError(error.message || 'Failed to load skill timeline.');
    } finally {
      setTimelineLoading(false);
    }
  };

  return (
    <div className="page page-shell page-stack">
      <section className="page-header">
        <div>
          <p className="eyebrow">Admin reporting</p>
          <h1>Mastery Analytics</h1>
          <p>Inspect student-level mastery summaries, progression reasoning, and skill timelines without changing product access boundaries.</p>
        </div>
      </section>

      <section className="card mastery-hero-card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Lookup</p>
            <h2>Student overview</h2>
          </div>
          <p className="muted-text">Use a numeric `student_id` to pull the current mastery snapshot, selection diagnostics, and skill timeline.</p>
        </div>
        <div className="mastery-lookup-row">
          <div className="mastery-lookup-field">
            <label htmlFor="mastery-student-id">Student ID</label>
            <input
              id="mastery-student-id"
              type="number"
              inputMode="numeric"
              value={studentIdInput}
              onChange={(event) => setStudentIdInput(event.target.value)}
              placeholder="Enter student_id"
            />
          </div>
          <button type="button" className="btn btn-primary" onClick={handleLoadOverview} disabled={overviewLoading}>
            {overviewLoading ? 'Loading...' : 'Load'}
          </button>
        </div>

        {overviewError ? <p role="alert" className="notice notice-error">{overviewError}</p> : null}

        {overview ? (
          <div className="page-stack mastery-stack" style={{ marginTop: 'var(--space-6)' }}>
            <section className="mastery-summary-grid">
              {summaryCards.map((item) => (
                <article key={item.label} className="mastery-summary-card">
                  <span className="mastery-summary-label">{item.label}</span>
                  <strong className="mastery-summary-value">{item.value || '-'}</strong>
                </article>
              ))}
            </section>

            <section className="card">
              <p className="section-title">Summary</p>
              <ul className="mastery-raw-list">
                <li>skills_total: {String(getSummaryValue(overview, 'skills_total'))}</li>
                <li>due_now: {String(getSummaryValue(overview, 'due_now'))}</li>
                <li>weak: {String(getSummaryValue(overview, 'weak'))}</li>
                <li>mastered: {String(getSummaryValue(overview, 'mastered'))}</li>
                <li>struggling: {String(getSummaryValue(overview, 'struggling'))}</li>
              </ul>
            </section>

            <section className="card">
              <p className="section-title">Progression Summary</p>
              {progressionError ? <p role="alert" className="notice notice-error">{progressionError}</p> : null}
              {progressionSummary ? (
                <div className="stack-sm">
                  <ul className="mastery-raw-list">
                    <li>total_attempts: {String(progressionSummary.total_attempts ?? '')}</li>
                    <li>progression_bucket_counts: {JSON.stringify(progressionSummary.progression_bucket_counts ?? {})}</li>
                    <li>selection_reason_counts: {JSON.stringify(progressionSummary.selection_reason_counts ?? {})}</li>
                    <li>selection_reason_detail_counts: {JSON.stringify(progressionSummary.selection_reason_detail_counts ?? {})}</li>
                    <li>reason_flag_counts: {JSON.stringify(progressionSummary.reason_flag_counts ?? {})}</li>
                    <li>family_counts: {JSON.stringify(progressionSummary.family_counts ?? {})}</li>
                    <li>macro_skill_counts: {JSON.stringify(progressionSummary.macro_skill_counts ?? {})}</li>
                    <li>micro_skill_counts: {JSON.stringify(progressionSummary.micro_skill_counts ?? {})}</li>
                    <li>sticky_context_counts: {JSON.stringify(progressionSummary.sticky_context_counts ?? {})}</li>
                    <li>axis_transition_summary: {JSON.stringify(progressionSummary.axis_transition_summary ?? {})}</li>
                    <li>target_alignment_summary: {JSON.stringify(progressionSummary.target_alignment_summary ?? {})}</li>
                  </ul>

                  {getEvidenceIntegritySummary(progressionSummary) ? (
                    <section className="card">
                      <div className="section-header">
                        <div>
                          <p className="section-title">Evidence Integrity</p>
                          <h3>Automatic vs tutor-adjusted evidence</h3>
                        </div>
                        <p className="muted-text">
                          Aggregate-only view of override-adjusted and low-confidence evidence. This does not change progression scoring.
                        </p>
                      </div>
                      {(() => {
                        const integrity = getEvidenceIntegritySummary(progressionSummary);
                        const familyRows = Array.isArray(integrity.by_family) ? integrity.by_family : [];
                        const macroRows = Array.isArray(integrity.by_macro_skill) ? integrity.by_macro_skill : [];
                        const microRows = Array.isArray(integrity.by_micro_skill) ? integrity.by_micro_skill : [];
                        return (
                          <div className="stack-sm">
                            <div className="mastery-skill-stat-grid">
                              <div>
                                <span>automatic evidence</span>
                                <strong>{String(integrity.automatic_attempt_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>automatic low-confidence</span>
                                <strong>{String(integrity.automatic_low_confidence_attempt_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>tutor-adjusted evidence</span>
                                <strong>{String(integrity.tutor_adjusted_attempt_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>tutor-adjusted low-confidence</span>
                                <strong>{String(integrity.tutor_adjusted_low_confidence_attempt_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>tutor-confirmed evidence</span>
                                <strong>{String(integrity.tutor_confirmed_attempt_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>confirmed low-confidence</span>
                                <strong>{String(integrity.tutor_confirmed_low_confidence_attempt_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>override rate</span>
                                <strong>{formatPercent(integrity.override_rate)}</strong>
                              </div>
                              <div>
                                <span>confirmation rate</span>
                                <strong>{formatPercent(integrity.confirmation_rate)}</strong>
                              </div>
                              <div>
                                <span>low-confidence rate</span>
                                <strong>{formatPercent(integrity.low_confidence_rate)}</strong>
                              </div>
                            </div>

                            <ul className="mastery-raw-list">
                              <li>evidence_integrity_counts: {JSON.stringify(integrity.evidence_integrity_counts ?? {})}</li>
                              <li>top_evidence_integrity: {JSON.stringify(integrity.top_evidence_integrity ?? null)}</li>
                            </ul>

                            <h3>Top Integrity Buckets</h3>
                            {[['Family', familyRows], ['Macro skill', macroRows], ['Micro skill', microRows]].map(([label, rows]) => (
                              <div key={label} className="stack-xs">
                                <p className="section-title">{label}</p>
                                {rows.length > 0 ? (
                                  <ul className="data-list">
                                    {rows.slice(0, 3).map((row, index) => (
                                      <li key={`${label}-${row.name ?? index}`} className="data-list-item">
                                        {String(row.name ?? 'unknown')}: attempts={String(row.attempt_count ?? 0)},
                                        tutor_adjusted={String(row.tutor_adjusted_attempt_count ?? 0)},
                                        tutor_confirmed={String(row.tutor_confirmed_attempt_count ?? 0)},
                                        low_confidence=
                                        {String(
                                          Number(row.automatic_low_confidence_attempt_count ?? 0) +
                                            Number(row.tutor_adjusted_low_confidence_attempt_count ?? 0)
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p>No integrity buckets available.</p>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </section>
                  ) : null}

                  {getProgressionIntegrityDiagnostics(progressionSummary) ? (
                    <section className="card">
                      <div className="section-header">
                        <div>
                          <p className="section-title">Progression Integrity Diagnostics</p>
                          <h3>Review signals for recent mastery updates</h3>
                        </div>
                        <p className="muted-text">
                          Review signal only; this does not alter progression scoring, weighting, or selection.
                        </p>
                      </div>
                      {(() => {
                        const diagnostics = getProgressionIntegrityDiagnostics(progressionSummary);
                        const reviewSignals = getIntegrityReviewSignals(progressionSummary) || {};
                        const affectedFamilies = Array.isArray(reviewSignals.affected_family_counts)
                          ? reviewSignals.affected_family_counts
                          : [];
                        return (
                          <div className="stack-sm">
                            <div className="mastery-skill-stat-grid">
                              <div>
                                <span>recent attempts</span>
                                <strong>{String(diagnostics.recent_attempt_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>low-confidence updates</span>
                                <strong>{String(diagnostics.low_confidence_update_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>tutor-adjusted updates</span>
                                <strong>{String(diagnostics.tutor_adjusted_update_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>adjusted after low-confidence</span>
                                <strong>{String(diagnostics.tutor_adjusted_low_confidence_update_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>tutor-confirmed updates</span>
                                <strong>{String(diagnostics.tutor_confirmed_update_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>confirmed low-confidence</span>
                                <strong>{String(diagnostics.tutor_confirmed_low_confidence_update_count ?? 0)}</strong>
                              </div>
                              <div>
                                <span>attention status</span>
                                <strong>{String(diagnostics.integrity_attention_status ?? 'no_issue')}</strong>
                              </div>
                            </div>

                            <ul className="mastery-raw-list">
                              <li>top_low_confidence_micro: {JSON.stringify(diagnostics.top_low_confidence_micro ?? null)}</li>
                              <li>top_tutor_adjusted_micro: {JSON.stringify(diagnostics.top_tutor_adjusted_micro ?? null)}</li>
                              <li>top_tutor_confirmed_micro: {JSON.stringify(diagnostics.top_tutor_confirmed_micro ?? null)}</li>
                              <li>top_affected_family: {JSON.stringify(diagnostics.top_affected_family ?? null)}</li>
                            </ul>

                            <h3>Integrity Review Signals</h3>
                            {affectedFamilies.length > 0 ? (
                              <ul className="data-list">
                                {affectedFamilies.slice(0, 5).map((row, index) => (
                                  <li key={`${row.name ?? 'family'}-${index}`} className="data-list-item">
                                    {String(row.name ?? 'unknown')}: affected_updates={String(row.count ?? 0)}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p>No progression integrity review signals in this window.</p>
                            )}
                          </div>
                        );
                      })()}
                    </section>
                  ) : null}

                  {getProgressionTrustAdvisory(progressionSummary) ? (
                    <section className="card">
                      <div className="section-header">
                        <div>
                          <p className="section-title">Progression Trust Advisory</p>
                          <h3>Aggregate review recommendation</h3>
                        </div>
                        <p className="muted-text">
                          Advisory only. Does not alter progression scoring.
                        </p>
                      </div>
                      {(() => {
                        const advisory = getProgressionTrustAdvisory(progressionSummary);
                        const topMicro = advisory.top_affected_micro;
                        const topFamily = advisory.top_affected_family;
                        return (
                          <div className="stack-sm">
                            <div className="mastery-skill-stat-grid">
                              <div>
                                <span>advisory status</span>
                                <strong>{String(advisory.advisory_status ?? 'high_trust')}</strong>
                              </div>
                              <div>
                                <span>primary reason</span>
                                <strong>{String(advisory.primary_reason ?? 'none')}</strong>
                              </div>
                              <div>
                                <span>recommended action</span>
                                <strong>{String(advisory.recommended_review_action ?? 'none')}</strong>
                              </div>
                              <div>
                                <span>confirmation coverage</span>
                                <strong>{formatPercent(advisory.confirmation_coverage_rate)}</strong>
                              </div>
                              <div>
                                <span>unresolved low-confidence</span>
                                <strong>{String(advisory.unresolved_low_confidence_attempt_count ?? 0)}</strong>
                              </div>
                            </div>
                            <ul className="mastery-raw-list">
                              <li>top_affected_micro: {JSON.stringify(topMicro ?? null)}</li>
                              <li>top_affected_family: {JSON.stringify(topFamily ?? null)}</li>
                            </ul>
                            {advisory.explanation ? (
                              <p className="muted-text">{String(advisory.explanation)}</p>
                            ) : null}
                          </div>
                        );
                      })()}
                    </section>
                  ) : null}

                  <h3>Top Resurfaced Micros</h3>
                  {Array.isArray(progressionSummary.top_resurfaced_micros) &&
                  progressionSummary.top_resurfaced_micros.length > 0 ? (
                    <ul className="data-list">
                      {progressionSummary.top_resurfaced_micros.map((micro, index) => (
                        <li key={`${micro.micro_skill_code ?? 'micro'}-${index}`} className="data-list-item">
                          {String(micro.micro_skill_code ?? '')}: attempts={String(micro.attempts ?? '')},
                          review={String(micro.review_count ?? '')}, sticky=
                          {String(micro.sticky_count ?? '')}, detour={String(micro.detour_count ?? '')}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No resurfaced micro summary available.</p>
                  )}
                </div>
              ) : null}
            </section>

            <section className="card">
              <div className="section-header">
                <div>
                  <p className="section-title">Skills</p>
                  <h3>Skill-level mastery state</h3>
                </div>
                <p className="muted-text">Load timeline detail from any row without leaving the overview.</p>
              </div>
              {skills.length === 0 ? (
                <p>No skills found.</p>
              ) : (
                <div className="mastery-skill-grid">
                  {skills.map((skill, index) => {
                    const skillId = skill.skill_id ?? skill.id ?? '';
                    return (
                      <article key={skillId || `skill-row-${index}`} className="mastery-skill-card">
                        <div className="list-item-header">
                          <div className="stack-xs">
                            <p className="section-title">skill_id {String(skill.skill_id ?? '')}</p>
                            <h3>{String(skill.skill_code ?? 'Unknown skill')}</h3>
                          </div>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleLoadTimeline(skillId)}
                            disabled={timelineLoading || !skillId}
                          >
                            Timeline
                          </button>
                        </div>
                        <div className="mastery-skill-stat-grid">
                          <div><span>order_index</span><strong>{String(skill.order_index ?? '')}</strong></div>
                          <div><span>mastery_score</span><strong>{String(skill.mastery_score ?? '')}</strong></div>
                          <div><span>stability_score</span><strong>{String(skill.stability_score ?? '')}</strong></div>
                          <div><span>current_cognitive_level</span><strong>{String(skill.current_cognitive_level ?? '')}</strong></div>
                          <div><span>current_realism_level</span><strong>{String(skill.current_realism_level ?? '')}</strong></div>
                          <div><span>ema_adj_percent</span><strong>{String(skill.ema_adj_percent ?? '')}</strong></div>
                          <div><span>ema_seconds_to_finalize</span><strong>{String(skill.ema_seconds_to_finalize ?? '')}</strong></div>
                          <div><span>recent_mastery_count</span><strong>{String(skill.recent_mastery_count ?? '')}</strong></div>
                          <div><span>recent_struggle_count</span><strong>{String(skill.recent_struggle_count ?? '')}</strong></div>
                          <div><span>generator_cap_constrained_micro_count</span><strong>{String(skill.generator_cap_constrained_micro_count ?? '')}</strong></div>
                          <div><span>generator_cap_constrained_mastered_micro_count</span><strong>{String(skill.generator_cap_constrained_mastered_micro_count ?? '')}</strong></div>
                          <div><span>has_generator_cap_constrained_mastery</span><strong>{String(skill.has_generator_cap_constrained_mastery ?? '')}</strong></div>
                          <div><span>next_due_at</span><strong>{String(skill.next_due_at ?? '')}</strong></div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        ) : null}
      </section>

      {timelineSkillId ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Timeline</p>
              <h2>Timeline for skill {timelineSkillId}</h2>
            </div>
          </div>
          {timelineLoading ? <p>Loading timeline...</p> : null}
          {timelineError ? <p role="alert" className="notice notice-error">{timelineError}</p> : null}
          {!timelineLoading && timelineSkillId && !timelineError ? (
            timeline.length > 0 ? (
              <ul className="data-list">
                {timeline.map((point, index) => (
                  <li key={point.attempt_id ?? `${timelineSkillId}-${index}`} className="data-list-item">
                    <div className="mastery-timeline-grid">
                      <div><span>finalized_at/created_at</span><strong>{String(point.finalized_at ?? point.created_at ?? '')}</strong></div>
                      <div><span>adjusted_percent</span><strong>{String(point.adjusted_percent ?? '')}</strong></div>
                      <div><span>raw_percent</span><strong>{String(point.raw_percent ?? '')}</strong></div>
                      <div><span>seconds_to_finalize</span><strong>{String(point.seconds_to_finalize ?? '')}</strong></div>
                      <div><span>similar_question_triggered</span><strong>{String(point.similar_question_triggered ?? '')}</strong></div>
                      <div><span>cognitive_level</span><strong>{String(point.cognitive_level ?? '')}</strong></div>
                      <div><span>realism_level</span><strong>{String(point.realism_level ?? '')}</strong></div>
                      <div><span>generation_source</span><strong>{String(point.generation_source ?? '')}</strong></div>
                      <div><span>generation_model</span><strong>{String(point.generation_model ?? '')}</strong></div>
                      <div><span>generation_prompt_version</span><strong>{String(point.generation_prompt_version ?? '')}</strong></div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No timeline points found.</p>
            )
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export default MasteryAnalyticsPage;
